import { useQueryClient } from '@tanstack/react-query';
import { type JSX, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  addArtifactToKnowledge,
  isAlreadyShelved,
  knowledgeSourceKeys,
} from '@/lib/api/workspace-knowledge.js';
import { notify } from '@/lib/notify.js';
import type { WorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';
import type { WorkspaceSharing } from '@/features/workspace/workspace-access.js';

import { artifactKnowledgeConfirmation } from './artifact-knowledge.js';

/**
 * Confirms putting one or more workspace files on the knowledge shelf, and does it. The confirmation is
 * mandatory because in a shared workspace the promotion also OPENS the file to every member — the dialog's
 * words are what tells the user that before it happens ({@link artifactKnowledgeConfirmation}).
 *
 * Both entries use it: the Artifacts section (a file's ⋮ menu and the selection bar) and the chat's file
 * panel. Nothing is copied and nothing is re-indexed — the row points at the file by id.
 *
 * A total failure keeps the dialog open with the service's own words (a 403 on a role that changed under
 * the caller, a 404 on a file deleted while the card was open), because there is nothing to show for it
 * behind the dialog. A partial failure in a bulk promotion closes and reports the count: the files that
 * did land are already on the shelf.
 *
 * "Already on the shelf" is not a failure at all: a 409 is where the user wanted the file to be, so it
 * counts with the ones that landed — the dialog closes, and it only says "already there" when that was
 * the outcome for every file.
 */
export function ArtifactKnowledgeDialog({
  workspaceId,
  files,
  sharing,
  onClose,
}: {
  workspaceId: string;
  files: readonly WorkspaceArtifact[];
  /** The workspace's sharing state, `unknown` included — the wording warns until it is known personal. */
  sharing: WorkspaceSharing;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { title, description } = artifactKnowledgeConfirmation(files, sharing);

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const results = await Promise.allSettled(
        files.map((file) => addArtifactToKnowledge(workspaceId, file.id)),
      );
      const rejected = results.filter((result) => result.status === 'rejected');
      /**
       * A file the shelf refused as already on it counts with the ones that landed, not with the
       * failures: the user's goal for it is met either way. Folding it into `failed` produced an error
       * toast for a bulk run in which every selected file ended up shelved — easy to hit while the
       * knowledge list is still resolving, which is exactly when a shelved file still looks promotable.
       */
      const alreadyThere = rejected.filter((result) =>
        isAlreadyShelved(result.reason),
      );
      const failed = rejected.filter(
        (result) => !isAlreadyShelved(result.reason),
      );
      /**
       * The knowledge list gains a row, and in a shared workspace the artifact rows gain a shared
       * visibility — the badge in the section behind this dialog would otherwise keep saying "Private".
       * It is refreshed even when everything was refused, because the most common refusal is a 409: the
       * file is on the shelf already, and the entry behind this dialog has to catch up and say so.
       */
      await queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
      await queryClient.invalidateQueries({
        queryKey: ['workspace-artifacts', workspaceId],
      });
      /**
       * Nothing landed and nothing was already there: the dialog stays open with the service's words,
       * because it is the only place they can be read.
       */
      if (failed.length === results.length) {
        setError(rejectionMessage(failed[0]));
        return;
      }
      if (failed.length > 0) {
        notify.error(
          `Couldn't add ${failed.length} of ${results.length} files to the knowledge base`,
        );
      } else if (alreadyThere.length === results.length) {
        notify.info(
          files.length === 1
            ? 'Already in the knowledge base'
            : 'These files are already in the knowledge base',
        );
      } else {
        notify.success(
          files.length === 1
            ? 'Added to the knowledge base'
            : `${files.length} files added to the knowledge base`,
        );
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
    >
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="brand" disabled={busy} onClick={() => void confirm()}>
          {busy ? 'Adding…' : 'Add to knowledge base'}
        </Button>
      </div>
    </AppDialog>
  );
}

/** The rejected promise's message, or a generic line when it carries something that is not an Error. */
function rejectionMessage(
  result: PromiseSettledResult<unknown> | undefined,
): string {
  if (result?.status === 'rejected' && result.reason instanceof Error) {
    return result.reason.message;
  }
  return 'Failed to add the file to the knowledge base.';
}
